import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal, MaskedLine, ScrollMaskedLine } from "@/components/Reveal";
import api from "@/lib/api";
import { usePageTitle } from "@/lib/usePageTitle";

const FORMATS = [
  {
    n: "01",
    title: "Mentheris Research Dialogues",
    text: "A recurring series of open conversations with researchers, practitioners, and builders working at the intersection of technology and human adaptation.",
    meta: "Recurring · Open format",
    to: "/research-dialogues",
  },
  {
    n: "02",
    title: "Roundtables",
    text: "Small, curated sessions bringing together decision-makers and researchers around a single transformation challenge, designed for candour, not presentation decks.",
    meta: "Curated · By invitation",
    to: "/roundtables",
  },
  {
    n: "03",
    title: "Reading / Research Circle",
    text: "A small circle working through essential texts on technology, adaptation, and human-centered systems, slowly and together.",
    meta: "In formation",
    to: "/reading-circle",
  },
];

function GatheringRow({ ev, i }) {
  const action = ev.status === "soon"
    ? ev.more_link
      ? { label: "View", href: ev.more_link }
      : null
    : ev.rsvp_link
      ? { label: "RSVP", href: ev.rsvp_link }
      : null;
  const formats = (ev.location || "").split("\u00b7").map((s) => s.trim()).filter(Boolean);
  const session = (ev.date || "").match(/session\s*\d+/i)?.[0];
  const dateRest = (ev.date || "").replace(/session\s*\d+\s*\u00b7\s*/i, "");

  return (
    <Reveal delay={i * 0.06}>
      <div
        className="group relative grid md:grid-cols-12 gap-3 md:gap-8 items-baseline py-8 md:py-9 px-2 -mx-2 transition-colors duration-500 hover:bg-ink/[0.025]"
        data-testid={`event-item-${i}`}
      >
        <span className="absolute -left-1 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-ember opacity-0 group-hover:opacity-100 transition-opacity duration-500 hidden md:block" aria-hidden="true" />
        <div className="md:col-span-2">
          {session && <span className="block text-[10px] tracking-[0.2em] uppercase text-fog/80">{session}</span>}
          <span className="block text-xs tracking-[0.15em] uppercase text-fog mt-1">{dateRest}</span>
        </div>
        <div className="md:col-span-7 md:pl-4">
          <p className="text-[10px] tracking-[0.2em] uppercase mb-1.5 text-ember">
            {ev.kind === "roundtable" ? "Roundtable" : "Research Dialogue"}
            {ev.status === "soon" ? " · Coming soon" : ""}
          </p>
          <h3 className="font-serif text-lg leading-snug transition-transform duration-500 group-hover:translate-x-1">
            {ev.title}
          </h3>
          <p className="mt-1.5 text-sm text-fog leading-relaxed max-w-xl md:whitespace-nowrap md:overflow-hidden md:text-ellipsis">
            {ev.description}
          </p>
        </div>
        <div className="md:col-span-3 flex md:justify-end items-baseline gap-6">
          <span className="flex flex-col md:items-end gap-1">
            {formats.map((f) => (
              <span key={f} className="text-xs tracking-[0.15em] uppercase text-fog">{f}</span>
            ))}
          </span>
          {action && (
            action.href.startsWith("/") ? (
              <Link
                to={action.href}
                data-testid={`event-action-${i}`}
                className="group/a inline-flex items-center gap-1.5 text-xs tracking-wide shrink-0 transition-colors duration-300 text-ink"
              >
                {action.label}
                <ArrowRight size={13} className="transition-transform duration-300 group-hover/a:translate-x-1" />
              </Link>
            ) : (
              <a
                href={action.href}
                data-testid={`event-action-${i}`}
                className="group/a inline-flex items-center gap-1.5 text-xs tracking-wide shrink-0 transition-colors duration-300 text-ink"
              >
                {action.label}
                <ArrowRight size={13} className="transition-transform duration-300 group-hover/a:translate-x-1" />
              </a>
            )
          )}
        </div>
      </div>
    </Reveal>
  );
}

export default function Community() {
  usePageTitle("Community | Mentheris", "Research Dialogues, Roundtables and the Research Circle: spaces where Mentheris research is examined collectively.");
  const [events, setEvents] = useState([]);
  const no = (e) => parseInt((e.date || "").match(/session\s*(\d+)/i)?.[1] || "0", 10);
  const upcoming = events.filter((e) => e.status !== "past").sort((a, b) => no(a) - no(b));

  useEffect(() => {
    api.get("/events").then(({ data }) => setEvents(data)).catch(() => {});
  }, []);

  return (
    <main className="pt-24 md:pt-32" data-testid="community-page">
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 pb-8 md:pb-12">
        <h1 className="font-serif font-medium tracking-tighter leading-[0.95] text-5xl mt-6" data-testid="community-headline">
          <MaskedLine delay={0.25}>Community</MaskedLine>
        </h1>
        <Reveal delay={0.55} className="mt-10 max-w-2xl">
          <p className="text-base md:text-lg text-fog leading-relaxed">
            Ideas develop differently when they are challenged, shared and explored with others.
            Mentheris creates spaces where emerging questions can be examined collectively,
            across research, practice and different points of view.
          </p>
        </Reveal>
      </section>


      <section data-testid="community-formats" className="bg-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 pt-2 pb-20 md:pb-28">
          <div className="flex flex-col items-start mb-12" data-testid="community-intro">
            <motion.span
              initial={{ scaleY: 0 }}
              whileInView={{ scaleY: 1 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="block w-px h-14 bg-ink/25 origin-top"
              aria-hidden="true"
            />
            <motion.span
              initial={{ opacity: 0, scale: 0 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: 1.0 }}
              className="block w-[7px] h-[7px] rounded-full bg-ember mt-1 mb-8 -ml-[3px]"
              aria-hidden="true"
            />
            <Reveal>
              <p className="text-xs tracking-[0.25em] uppercase text-fog mb-6">How we build community</p>
            </Reveal>
            <h2 className="font-serif text-3xl md:text-5xl tracking-tight max-w-3xl" data-testid="community-intro-heading">
              <ScrollMaskedLine>Different questions need</ScrollMaskedLine>
              <ScrollMaskedLine delay={0.15}>different kinds of conversation.</ScrollMaskedLine>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 border-t border-l border-line">
            {FORMATS.map((f, i) => (
              <Reveal key={f.n} delay={i * 0.08}>
                <Link
                  to={f.to}
                  data-testid={`community-tile-${f.n}`}
                  className="group relative block border-r border-b border-line p-8 md:p-10 min-h-[360px] h-full flex flex-col justify-between overflow-hidden hover:bg-ink hover:text-bone transition-colors duration-500"
                >
                  <span className="absolute top-0 left-0 h-[3px] w-0 bg-leaf transition-all duration-500 ease-out group-hover:w-full" aria-hidden="true" />
                  <div>
                    <div className="flex items-center justify-between">
                      <span className="font-serif text-5xl text-ink/15 group-hover:text-bone/25 transition-colors duration-500">{f.n}</span>
                      <span className="w-2 h-2 rounded-full bg-leaf opacity-0 scale-50 group-hover:opacity-100 group-hover:scale-100 transition-all duration-500" aria-hidden="true" />
                    </div>
                    <h3 className="font-serif text-2xl md:text-[1.8rem] mt-10 leading-snug">{f.title}</h3>
                    <p className="mt-4 text-sm text-fog group-hover:text-bone/70 leading-relaxed transition-colors duration-500">{f.text}</p>
                  </div>
                  <div className="flex items-center justify-between mt-10">
                    <span className="text-[10px] tracking-[0.2em] uppercase text-fog group-hover:text-bone/60 transition-colors duration-500">{f.meta}</span>
                    <ArrowUpRight size={20} className="transition-transform duration-500 group-hover:translate-x-1.5 group-hover:-translate-y-1.5 group-hover:text-leaf" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {upcoming.length > 0 && (
        <section className="relative" data-testid="gatherings-section">
          <span className="absolute left-[6%] top-[12%] w-1.5 h-1.5 rounded-full bg-ink/15 hidden md:block" style={{ animation: "drift 9s ease-in-out infinite" }} aria-hidden="true" />
          <span className="absolute right-[10%] top-[45%] w-1 h-1 rounded-full bg-ember/40 hidden md:block" style={{ animation: "drift 11s ease-in-out 1.2s infinite" }} aria-hidden="true" />
          <span className="absolute left-[14%] bottom-[18%] w-1 h-1 rounded-full bg-ink/15 hidden md:block" style={{ animation: "drift 10s ease-in-out 2s infinite" }} aria-hidden="true" />
          <div className="mx-auto max-w-[1400px] px-6 md:px-12 pt-20 md:pt-28 pb-12 md:pb-16">
            <div data-testid="upcoming-events-section">
              <Reveal>
                <div className="flex items-baseline justify-between mb-6">
                  <h2 className="font-serif text-4xl tracking-tight">Upcoming gatherings</h2>
                  <Link to="/research-dialogues" data-testid="calendar-link" className="group/cal inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase text-fog hover:text-ink transition-colors duration-300">
                    Calendar
                    <ArrowRight size={13} className="transition-transform duration-300 group-hover/cal:translate-x-1" />
                  </Link>
                </div>
              </Reveal>
              <div>
                {upcoming.map((ev, i) => (
                  <GatheringRow key={ev.id} ev={ev} i={i} />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="bg-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 pt-14 md:pt-20 pb-24 md:pb-32">
        <Reveal>
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-9">
              <p className="font-serif text-2xl md:text-4xl leading-[1.25] tracking-tight max-w-3xl">
                If you are working on the human side of technological change, in research, in
                practice, or in policy, we would like to hear from you.
              </p>
              <a
                href="mailto:contact@mentheris.com"
                data-testid="community-contact-cta"
                className="group inline-flex items-center gap-3 bg-ink text-bone px-8 py-4 text-sm tracking-wide hover:bg-ember transition-colors duration-300 mt-12"
              >
                Start a conversation
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
            </div>
          </div>
        </Reveal>
        </div>
      </section>

    </main>
  );
}
